/**
 * reportDetailConfig.js
 *
 * Shared configuration, options, and JSDoc data models for Institution Report Detail.
 * Follows docs/fe-report-detail-integration.md schema.
 */

export const YEAR_RANGE_OPTIONS = [
  { label: "All time", value: "all" },
  { label: "Last 5 years", value: "last_5_years" },
  { label: "Last 10 years", value: "last_10_years" },
];

export const DEFAULT_REPORT_FILTERS = {
  yearRange: "all",
  subjectArea: [],
};

// ==================== JSDoc Type Definitions ====================

/**
 * @typedef {"all" | "last_5_years" | "last_10_years"} YearRange
 */

/**
 * @typedef {Object} ReportDetailRequest
 * @property {string} entity_id - Institution ID
 * @property {YearRange} [year_range] - Time range
 * @property {string[]} [subject_area] - Array containing at most 1 keyword
 */

/**
 * @typedef {Object} EntityCard
 * @property {string} id
 * @property {string} name
 * @property {string|null} logo_url
 * @property {string|null} state
 * @property {string} entity_type
 * @property {string|null} subject_area_filter
 * @property {string} year_range
 */

/**
 * @typedef {Object} InstitutionKPIs
 * @property {number} publications
 * @property {number} citations
 * @property {number} researchers
 * @property {number} h_index
 */

/**
 * @typedef {Object} OverviewTopicItem
 * @property {string|null} id
 * @property {number|null} [int_id]
 * @property {string} keyword
 * @property {number|null} [level]
 * @property {number|null} [h_index]
 * @property {number} publication_count
 * @property {number|null} [citation_count]
 * @property {number|null} [rank]
 * @property {string|null} [scope]
 */

/**
 * @typedef {OverviewTopicItem & { growth_rate: number, series: number[] }} ActiveTopicItem
 */

/**
 * @typedef {Object} ResearchStrength
 * @property {OverviewTopicItem[]} top_topics
 * @property {ActiveTopicItem[]} top_active_topics
 */

/**
 * @typedef {Object} OverviewFacultyItem
 * @property {string} id
 * @property {string} name
 * @property {string|null} position
 * @property {string|null} photo_url
 * @property {number|null} h_index
 * @property {number|null} grants_count
 * @property {number|null} total_amount
 * @property {string|null} explore_slug
 */

/**
 * @typedef {Object} TopFacultyOverview
 * @property {OverviewFacultyItem[]} by_h_index
 * @property {OverviewFacultyItem[]} by_grants
 */

/**
 * @typedef {Object} SponsorItem
 * @property {string} id
 * @property {string} name
 * @property {number} total_amount
 * @property {number} grant_count
 * @property {number} share_pct
 */

/**
 * @typedef {Object} TopSponsorsOverview
 * @property {SponsorItem[]} items
 * @property {number} unique_sponsors
 */

/**
 * @typedef {Object} FeaturedGrantItem
 * @property {string} id
 * @property {string} award_title
 * @property {number|null} amount_awarded
 * @property {string|null} start_date
 * @property {string|null} end_date
 * @property {SponsorItem[]} sponsors
 */

/**
 * @typedef {Object} ReportDetailResponse
 * @property {EntityCard} entity
 * @property {ResearchStrength|null} research_strength
 * @property {InstitutionKPIs|null} kpis
 * @property {OverviewTopicItem[]|null} top_ranking
 * @property {TopFacultyOverview|null} top_faculty
 * @property {TopSponsorsOverview|null} top_sponsors
 * @property {FeaturedGrantItem[]|null} featured_grants
 * @property {Record<string, boolean>|null} [hidden_fields]
 * @property {Record<string, string>} [errors]
 * @property {number} time_taken
 */
